// src/components/EditPersonnelDialog.js
import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  Button,
  Alert,
  CircularProgress,
  Box,
  Typography,
  Divider,
} from '@mui/material';
import { updatePersonnel } from '../services/personnelService';

/**
 * Take the date part of an ISO timestamp WITHOUT going through `new Date()`.
 * Building a Date here would re-interpret midnight UTC in the browser's own
 * timezone and shift the day by one — the exact fault that put every stored
 * date a day behind. Slicing the string cannot do that.
 */
const toDateInput = (value) => {
  if (!value) return '';
  const s = String(value);
  const m = s.match(/^(\d{4}-\d{2}-\d{2})/);
  return m ? m[1] : '';
};

/**
 * A date input gives "1972-12-25"; Prisma needs a full ISO-8601 DateTime.
 * The trailing Z pins it to UTC midnight — without it the value would be read
 * as Lagos local time and stored as the previous day.
 */
const toISODateTime = (value) => {
  if (!value) return null;
  return `${value}T00:00:00.000Z`;
};

const EditPersonnelDialog = ({ open, personnel, onClose, onSuccess, onNotify }) => {
  const [form, setForm] = useState(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (open && personnel) {
      setForm({
        serviceNumber: personnel.serviceNumber || '',
        ippisNumber: personnel.ippisNumber || '',
        firstName: personnel.firstName || '',
        middleName: personnel.middleName || '',
        lastName: personnel.lastName || '',
        gender: personnel.gender || '',
        birthDate: toDateInput(personnel.birthDate),
        rankName: personnel.rankName || '',
        gradeName: personnel.gradeName || '',
        gradeLevel: personnel.gradeLevel || '',
        gradeStep: personnel.gradeStep || '',
        dateOfFirstAppointment: toDateInput(personnel.dateOfFirstAppointment),
        expectedRetirementDate: toDateInput(personnel.expectedRetirementDate),
        bankName: personnel.bankName || '',
        accountNumber: personnel.accountNumber || '',
        pfaName: personnel.pfaName || '',
        pencomId: personnel.pencomId || '',
        nhfNumber: personnel.nhfNumber || '',
        phoneNumber: personnel.phoneNumber || '',
        email: personnel.email || '',
      });
      setError('');
    }
  }, [open, personnel]);

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
    setError('');
  };

  const handleSubmit = async () => {
    setError('');
    // These are the NOT NULL columns on the personnel table.
    const required = [
      ['firstName', 'First Name'],
      ['lastName', 'Last Name'],
      ['gender', 'Gender'],
      ['rankName', 'Rank'],
      ['gradeName', 'Grade Name'],
      ['gradeLevel', 'Grade Level'],
      ['gradeStep', 'Grade Step'],
      ['bankName', 'Bank Name'],
      ['accountNumber', 'Account Number'],
      ['pfaName', 'PFA Name'],
      ['pencomId', 'PENCOM ID'],
    ];
    for (const [key, label] of required) {
      if (!form[key] || !form[key].toString().trim()) {
        setError(`${label} is required — enter NILL if the value is genuinely unknown`);
        return;
      }
    }

    setLoading(true);
    try {
      // serviceNumber, ippisNumber and commandId are deliberately not sent:
      // they are identity/placement keys and changing them here would break
      // reconciliation against the payroll master.
      await updatePersonnel(personnel.id, {
        firstName: form.firstName.trim(),
        middleName: form.middleName.trim() || null,
        lastName: form.lastName.trim(),
        gender: form.gender,
        birthDate: toISODateTime(form.birthDate),
        rankName: form.rankName.trim(),
        gradeName: form.gradeName.trim(),
        gradeLevel: form.gradeLevel.trim(),
        gradeStep: form.gradeStep.trim(),
        dateOfFirstAppointment: toISODateTime(form.dateOfFirstAppointment),
        expectedRetirementDate: toISODateTime(form.expectedRetirementDate),
        bankName: form.bankName.trim(),
        accountNumber: form.accountNumber.trim(),
        pfaName: form.pfaName.trim(),
        pencomId: form.pencomId.trim(),
        nhfNumber: form.nhfNumber.trim() || null,
        phoneNumber: form.phoneNumber.trim() || null,
        email: form.email.trim() || null,
      });
      const name = `${form.firstName} ${form.lastName}`.trim();
      if (onNotify) onNotify(`${name} updated successfully`, 'success');
      onSuccess();
    } catch (err) {
      const msg = err.response?.data?.message || 'Failed to update personnel';
      setError(msg);
      if (onNotify) onNotify(msg, 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (!loading) {
      setError('');
      onClose();
    }
  };

  if (!form) return null;

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle>
        Edit Personnel
        <Typography variant="body2" color="text.secondary">
          {form.serviceNumber} &middot; {`${form.firstName} ${form.lastName}`.trim()}
        </Typography>
      </DialogTitle>
      <DialogContent>
        <Box sx={{ mt: 1 }}>
          {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Service Number, IPPIS Number and Command cannot be changed here — they
            identify the officer against the payroll master and their command.
            Raise a transfer request to move an officer to another command.
          </Typography>

          <Divider sx={{ mb: 2 }}>Identity</Divider>
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
            <TextField label="Service Number" value={form.serviceNumber} size="small" sx={{ flex: '1 1 30%' }} disabled />
            <TextField label="IPPIS Number" value={form.ippisNumber} size="small" sx={{ flex: '1 1 30%' }} disabled />
            <TextField select label="Gender" name="gender" value={form.gender} onChange={handleChange} required size="small" sx={{ flex: '1 1 30%' }} disabled={loading}>
              <MenuItem value="MALE">Male</MenuItem>
              <MenuItem value="FEMALE">Female</MenuItem>
            </TextField>
            <TextField label="First Name" name="firstName" value={form.firstName} onChange={handleChange} required size="small" sx={{ flex: '1 1 30%' }} disabled={loading} />
            <TextField label="Middle Name" name="middleName" value={form.middleName} onChange={handleChange} size="small" sx={{ flex: '1 1 30%' }} disabled={loading} />
            <TextField label="Last Name" name="lastName" value={form.lastName} onChange={handleChange} required size="small" sx={{ flex: '1 1 30%' }} disabled={loading} />
            <TextField label="Date of Birth" name="birthDate" type="date" value={form.birthDate} onChange={handleChange} size="small" sx={{ flex: '1 1 30%' }} InputLabelProps={{ shrink: true }} disabled={loading} />
          </Box>

          <Divider sx={{ my: 2 }}>Employment</Divider>
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
            <TextField label="Rank" name="rankName" value={form.rankName} onChange={handleChange} required size="small" sx={{ flex: '1 1 30%' }} disabled={loading} />
            <TextField label="Grade Name" name="gradeName" value={form.gradeName} onChange={handleChange} required size="small" sx={{ flex: '1 1 30%' }} disabled={loading} placeholder="e.g. CONPASS" />
            <TextField label="Grade Level" name="gradeLevel" value={form.gradeLevel} onChange={handleChange} required size="small" sx={{ flex: '1 1 13%' }} disabled={loading} placeholder="e.g. 08" />
            <TextField label="Grade Step" name="gradeStep" value={form.gradeStep} onChange={handleChange} required size="small" sx={{ flex: '1 1 13%' }} disabled={loading} placeholder="e.g. 03" />
            <TextField label="Date of First Appointment" name="dateOfFirstAppointment" type="date" value={form.dateOfFirstAppointment} onChange={handleChange} size="small" sx={{ flex: '1 1 30%' }} InputLabelProps={{ shrink: true }} disabled={loading} />
            <TextField label="Expected Retirement Date" name="expectedRetirementDate" type="date" value={form.expectedRetirementDate} onChange={handleChange} size="small" sx={{ flex: '1 1 30%' }} InputLabelProps={{ shrink: true }} disabled={loading} />
          </Box>

          <Divider sx={{ my: 2 }}>Financial</Divider>
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
            <TextField label="Bank Name" name="bankName" value={form.bankName} onChange={handleChange} required size="small" sx={{ flex: '1 1 30%' }} disabled={loading} />
            <TextField label="Account Number" name="accountNumber" value={form.accountNumber} onChange={handleChange} required size="small" sx={{ flex: '1 1 30%' }} disabled={loading} />
            <TextField label="PFA Name" name="pfaName" value={form.pfaName} onChange={handleChange} required size="small" sx={{ flex: '1 1 30%' }} disabled={loading} helperText="Enter NILL if unknown" />
            <TextField label="PENCOM ID" name="pencomId" value={form.pencomId} onChange={handleChange} required size="small" sx={{ flex: '1 1 30%' }} disabled={loading} helperText="Enter NILL if unknown" />
            <TextField label="NHF Number" name="nhfNumber" value={form.nhfNumber} onChange={handleChange} size="small" sx={{ flex: '1 1 30%' }} disabled={loading} helperText="May be left blank" />
          </Box>

          <Divider sx={{ my: 2 }}>Contact (optional)</Divider>
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
            <TextField label="Phone Number" name="phoneNumber" value={form.phoneNumber} onChange={handleChange} size="small" sx={{ flex: '1 1 30%' }} disabled={loading} />
            <TextField label="Email" name="email" type="email" value={form.email} onChange={handleChange} size="small" sx={{ flex: '1 1 30%' }} disabled={loading} />
          </Box>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={loading}>Cancel</Button>
        <Button onClick={handleSubmit} variant="contained" disabled={loading}>
          {loading ? <CircularProgress size={24} /> : 'Save Changes'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default EditPersonnelDialog;